import React from 'react';

export default function SmsConsentPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="sms-consent-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <a href="/" className="text-xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>HostCockpit</a>
          <span className="text-xs text-zinc-500">SMS Opt-In & Terms</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-10">
        <section>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0052FF] mb-3">SMS Consent & Opt-In Policy</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-zinc-950 tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            How SMS Consent Works at HostCockpit
          </h1>
          <p className="text-base text-zinc-600 max-w-2xl">
            HostCockpit is a property-management platform used by Airbnb / short-term rental hosts to communicate with their guests.
            All SMS traffic on our platform is either (a) guest-initiated replies or (b) transactional messages sent only to guests who
            have checked an explicit opt-in checkbox at the time of booking. This page exists so prospective guests, property owners,
            and regulators can review our full opt-in flow at any time.
          </p>
        </section>

        <section className="border border-zinc-200 p-6 bg-zinc-50">
          <h2 className="text-lg font-bold text-zinc-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>1. Message Types</h2>
          <div className="space-y-4 text-sm text-zinc-700 leading-relaxed">
            <div>
              <p className="font-bold text-zinc-950 mb-1">AI Concierge Replies (conversational, guest-initiated)</p>
              <p>A guest texts our toll-free concierge number <strong>+1 (888) 985-7121</strong> with a question (check-in time,
                WiFi password, local recommendations, maintenance issue, etc.). Our AI replies using the property owner's
                knowledge base. We only reply after the guest texts us first; their first inbound message is their affirmative opt-in.</p>
            </div>
            <div>
              <p className="font-bold text-zinc-950 mb-1">Post-Stay Review Request SMS (transactional, one-off)</p>
              <p>Within a few days of a guest's checkout, the property owner may send a single SMS asking the guest to leave a
                Google review of the stay. This message is only delivered to guests who have ticked the SMS consent checkbox
                at booking time (see Section 2). No promotional, marketing, or third-party content is ever sent.</p>
            </div>
          </div>
        </section>

        <section className="border border-zinc-200 p-6">
          <h2 className="text-lg font-bold text-zinc-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>2. Opt-In Mechanism</h2>
          <p className="text-sm text-zinc-700 mb-4">
            Property owners collect guest contact details in HostCockpit's booking form. The form includes a dedicated,
            unchecked-by-default consent checkbox with the following exact wording. A booking cannot be submitted with a phone number unless the box is ticked:
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-5 mb-4" data-testid="opt-in-disclosure">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-5 h-5 border-2 border-[#0052FF] bg-[#0052FF] flex items-center justify-center rounded-sm mt-0.5">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div className="text-sm text-zinc-900">
                <p className="font-bold mb-1.5">SMS Consent</p>
                <p className="leading-relaxed">
                  I agree to receive transactional SMS messages from HostCockpit related to my stay, including an optional post-checkout
                  message with a link to leave a Google review. Message frequency is low (typically 0-2 messages per stay).
                  Message &amp; data rates may apply. Reply <strong>STOP</strong> to unsubscribe or <strong>HELP</strong> for help.
                  See our <a href="/sms-consent" className="underline text-[#0052FF]">SMS Terms</a> and <a href="/privacy" className="underline text-[#0052FF]">Privacy Policy</a>.
                </p>
              </div>
            </div>
          </div>
          <p className="text-xs text-zinc-500">Each opted-in booking is stored with a timestamp (<code className="text-[11px] bg-zinc-100 px-1 py-0.5 rounded">sms_consent_at</code>) that serves as immutable proof of consent. Opt-ins are never transferred or sold.</p>
        </section>

        <section className="border border-zinc-200 p-6">
          <h2 className="text-lg font-bold text-zinc-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>3. Sample Messages</h2>
          <div className="space-y-3 text-sm text-zinc-700">
            <div className="bg-zinc-50 border-l-2 border-[#0052FF] pl-4 py-2">
              <p className="text-xs font-bold text-zinc-500 mb-0.5">Guest → HostCockpit (inbound)</p>
              <p>Hi, what time is check-in?</p>
            </div>
            <div className="bg-zinc-50 border-l-2 border-emerald-500 pl-4 py-2">
              <p className="text-xs font-bold text-zinc-500 mb-0.5">HostCockpit AI → Guest (reply)</p>
              <p>Check-in is at 3:00 PM. Use the lockbox code 1234 on the front door. — I'm here anytime. Just say Hi or ask me anything.</p>
            </div>
            <div className="bg-zinc-50 border-l-2 border-amber-500 pl-4 py-2">
              <p className="text-xs font-bold text-zinc-500 mb-0.5">HostCockpit → Guest (post-stay review request, opt-in required)</p>
              <p>Hi Alex, hope you loved your stay at Oceanfront Villa! If you have 30 seconds, would you mind leaving us a Google review? https://search.google.com/local/writereview?placeid=... — Reply STOP to opt out.</p>
            </div>
          </div>
        </section>

        <section className="border border-zinc-200 p-6">
          <h2 className="text-lg font-bold text-zinc-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>4. Opt-Out (STOP / UNSUBSCRIBE)</h2>
          <p className="text-sm text-zinc-700 leading-relaxed">
            Replying <strong>STOP</strong>, <strong>UNSUBSCRIBE</strong>, <strong>END</strong>, <strong>QUIT</strong>, or <strong>CANCEL</strong> to any
            HostCockpit SMS will immediately end all messaging to that number. Opt-outs are handled automatically by our messaging provider (Twilio)
            and honoured system-wide within seconds. Reply <strong>HELP</strong> at any time for assistance, or email <a href="mailto:hosts@hostcockpit.com" className="underline text-[#0052FF]">hosts@hostcockpit.com</a>.
          </p>
        </section>

        <section className="border border-zinc-200 p-6">
          <h2 className="text-lg font-bold text-zinc-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>5. Message Frequency & Fees</h2>
          <ul className="list-disc list-inside text-sm text-zinc-700 space-y-1">
            <li>Typical: 0-5 SMS per guest stay, all transactional.</li>
            <li>No recurring promotional or marketing content is sent.</li>
            <li>Standard message &amp; data rates may apply depending on the guest's carrier plan. HostCockpit does not charge guests for SMS.</li>
          </ul>
        </section>

        <section className="border border-zinc-200 p-6">
          <h2 className="text-lg font-bold text-zinc-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>6. Privacy &amp; Data Handling</h2>
          <p className="text-sm text-zinc-700 leading-relaxed">
            Phone numbers and consent timestamps are used solely to deliver the messages described above and are never sold, shared with
            third parties for marketing, or used for promotional purposes. See our full <a href="/privacy" className="underline text-[#0052FF]">Privacy Policy</a>.
          </p>
        </section>

        <footer className="pt-6 border-t border-zinc-200 text-xs text-zinc-500">
          Last updated: April 2026 · Questions? <a href="mailto:hosts@hostcockpit.com" className="underline">hosts@hostcockpit.com</a>
        </footer>
      </main>
    </div>
  );
}
